import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTRPCAPI } from '../../../../trpc/trpc';

/** The parts of `outreach.*` that a change can make stale. */
export type OutreachArea =
  | 'home'
  | 'profile'
  | 'targets'
  | 'contacts'
  | 'lists'
  | 'campaigns'
  | 'review'
  | 'inbox'
  | 'mailboxes'
  /** `outreach.campaigns.live` only (design t1, t2): the search counters while a find runs. */
  | 'live';

/**
 * Reads the named `outreach.*` queries again (every one when no area is named). A write calls it
 * after its answer; `useOutreachRealtime` calls it for each event.
 */
export function useInvalidateOutreach(): (areas?: OutreachArea[]) => void {
  const trpcAPI = useTRPCAPI();
  const queryClient = useQueryClient();

  return useCallback(
    (areas?: OutreachArea[]) => {
      if (areas === undefined) {
        queryClient.invalidateQueries({ queryKey: trpcAPI.outreach.pathKey() }).catch(console.error);
        return;
      }
      const keys = {
        home: trpcAPI.outreach.home.pathKey(),
        profile: trpcAPI.outreach.profile.pathKey(),
        targets: trpcAPI.outreach.targets.pathKey(),
        contacts: trpcAPI.outreach.contacts.pathKey(),
        lists: trpcAPI.outreach.lists.pathKey(),
        campaigns: trpcAPI.outreach.campaigns.pathKey(),
        review: trpcAPI.outreach.review.pathKey(),
        inbox: trpcAPI.outreach.inbox.pathKey(),
        mailboxes: trpcAPI.outreach.mailboxes.pathKey(),
        live: trpcAPI.outreach.campaigns.live.pathKey(),
      };
      areas.forEach((area) => {
        queryClient.invalidateQueries({ queryKey: keys[area] }).catch(console.error);
      });
    },
    [queryClient, trpcAPI]
  );
}
